import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { type Answer, type MyEvent, useReply } from '../api/events';
import { useReadOnly } from '../auth/session';
import { dateLocale } from '../i18n';
import { daysUntil, formatDayLong, formatDayMonth, formatDayShort, formatEventDates } from '../lib/eventDates';
import ui from '../styles/ui.module.css';
import styles from './HomeEvents.module.css';

const ANSWERS: readonly Answer[] = ['ATTENDING', 'NOT_ATTENDING'];

/** Katılıyorum / Katılmıyorum with an optional note to the director; saved at once. */
export function AnswerForm({ choirId, event, onDone }: { choirId: string; event: MyEvent; onDone?: () => void }) {
  const { t } = useTranslation();
  const reply = useReply(choirId);
  const readOnly = useReadOnly();
  const [note, setNote] = useState(event.note ?? '');
  return (
    <div className={styles.answerForm} data-testid="answer-form">
      <div className={styles.answers} role="group" aria-label={event.name}>
        {ANSWERS.map((answer) => (
          <button
            key={answer}
            type="button"
            className={event.answer === answer ? `${ui.button} ${styles.answerOn}` : ui.button}
            aria-pressed={event.answer === answer}
            disabled={reply.isPending || readOnly}
            onClick={() =>
              reply.mutate({ eventId: event.id, answer, note: note.trim() || null }, { onSuccess: () => onDone?.() })
            }
          >
            {t(`myEvents.answer.${answer}`)}
          </button>
        ))}
      </div>
      <label className={ui.field}>
        <span className={ui.label}>{t('myEvents.note')}</span>
        <input
          className={ui.input}
          value={note}
          maxLength={500}
          disabled={readOnly}
          placeholder={readOnly ? t('viewAs.off') : undefined}
          onChange={(e) => setNote(e.target.value)}
        />
      </label>
      {reply.isError && (
        <div className={`${ui.notice} ${ui.noticeError}`} role="alert">
          {t('myEvents.replyFailed')}
        </div>
      )}
    </div>
  );
}

/** The next event the chorist takes part in: date, venue, their answer (changeable) and readiness. */
export function NextEventCard({ choirId, event }: { choirId: string; event: MyEvent }) {
  const { t } = useTranslation();
  const [changing, setChanging] = useState(false);
  const locale = dateLocale();
  const ready = event.songs.filter((s) => s.grade === 'READY').length;
  const asking = changing || !event.answer;
  return (
    <section className={`${ui.card} ${styles.next}`} data-testid="next-event">
      <div className={styles.nextHeader}>
        <div>
          <div className={ui.eyebrow}>{t('myEvents.nextEvent', { type: t(`eventTypes.${event.type}`) })}</div>
          <h2 className={styles.nextTitle}>
            <Link to={`/events/${event.id}`} className={styles.titleLink}>
              {event.name}
            </Link>
          </h2>
          <div className={ui.muted}>
            {[formatEventDates(event.startsOn, event.endsOn, locale), event.venue].filter(Boolean).join(' · ')}
            {' · '}
            {event.answer ? (
              <>
                <strong className={styles.answerText}>{t(`myEvents.yourAnswer.${event.answer}`)}</strong>
                {!changing && (
                  <>
                    {' · '}
                    <button type="button" className={ui.linkButton} onClick={() => setChanging(true)}>
                      {t('myEvents.change')}
                    </button>
                  </>
                )}
              </>
            ) : (
              <strong className={styles.awaiting}>{t('myEvents.awaiting')}</strong>
            )}
          </div>
          {event.callAndDress && <div className={ui.muted}>{event.callAndDress}</div>}
        </div>
        {event.readinessTracking && event.songs.length > 0 && (
          <div className={styles.readyBox}>
            <span className={styles.readyNumber}>
              {ready} / {event.songs.length}
            </span>
            <span className={ui.muted}>{t('myEvents.readyLabel')}</span>
          </div>
        )}
      </div>
      {asking && <AnswerForm choirId={choirId} event={event} onDone={() => setChanging(false)} />}
    </section>
  );
}

/** Songs the director asked the chorist to submit, soonest deadline first. */
export function RequestedSubmissions({ events }: { events: MyEvent[] }) {
  const { t } = useTranslation();
  const locale = dateLocale();
  const requests = events
    .filter((e) => e.participation === 'IN' && e.answer !== 'NOT_ATTENDING')
    .flatMap((e) => e.songs.filter((s) => s.submissionRequested && !s.submittedSinceRequest && s.submissionDeadline).map((s) => ({ event: e, song: s })))
    .filter(({ song }) => daysUntil(song.submissionDeadline as string) >= 0)
    .sort((a, b) => (a.song.submissionDeadline as string).localeCompare(b.song.submissionDeadline as string));
  if (requests.length === 0) return null;
  return (
    <section className={ui.card} data-testid="requested-submissions">
      <h2 className={ui.cardTitle}>{t('myEvents.requested')}</h2>
      <ul className={styles.list}>
        {requests.map(({ event, song }) => {
          const deadline = song.submissionDeadline as string;
          const days = daysUntil(deadline);
          const left = days === 0 ? t('myEvents.today') : days === 1 ? t('myEvents.tomorrow') : t('myEvents.daysLeft', { count: days });
          return (
            <li key={`${event.id}/${song.songId}`} data-testid="requested-item">
              <div>
                <strong>{song.title}</strong>
                <span className={ui.secondary}>
                  {event.name} · {t('myEvents.due', { date: formatDayLong(deadline, locale), left })}
                </span>
              </div>
              <Link to={`/songs/${song.songId}`} className={`${ui.button} ${ui.primary}`}>
                {t('myEvents.record')}
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** The chorist's other upcoming events, with whether they take part, in neutral words. */
export function UpcomingEvents({ events }: { events: MyEvent[] }) {
  const { t } = useTranslation();
  const locale = dateLocale();
  if (events.length === 0) return null;
  return (
    <section className={ui.card} data-testid="upcoming-events">
      <h2 className={ui.cardTitle}>{t('myEvents.upcoming')}</h2>
      <ul className={styles.list}>
        {events.map((event) => {
          const ready = event.songs.filter((s) => s.grade === 'READY').length;
          return (
            <li key={event.id} data-testid="upcoming-event">
              <span className={styles.date}>{formatDayShort(event.startsOn, locale)}</span>
              <div className={styles.grow}>
                <Link to={`/events/${event.id}`} className={styles.eventLink}>
                  {event.name}
                </Link>{' '}
                <span className={`${ui.chip} ${ui.chipNeutral}`}>{t(`eventTypes.${event.type}`)}</span>
                <span className={ui.secondary} data-testid="upcoming-status">
                  {event.participation === 'NOT_IN_LINEUP' && t('myEvents.notInLineup')}
                  {event.participation === 'EXCLUDED' && (
                    <>
                      {t('myEvents.excluded')}
                      {event.exclusionReason && ` · ${t('myEvents.excludedReason', { reason: event.exclusionReason })}`}
                    </>
                  )}
                  {event.participation === 'IN' &&
                    [
                      event.answer ? t(`myEvents.answer.${event.answer}`) : t('myEvents.awaiting'),
                      event.limitedLineup ? t('myEvents.inLineup') : null,
                      event.readinessTracking && event.songs.length > 0 ? t('myEvents.songsReady', { count: event.songs.length, ready }) : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** Wireframe 01's side box: an event in the chorist's line-up that has no answer yet. */
export function AwaitingReply({ choirId, event }: { choirId: string; event: MyEvent }) {
  const { t } = useTranslation();
  const locale = dateLocale();
  return (
    <section className={`${ui.card} ${styles.awaitingBox}`} data-testid="awaiting-reply">
      <div className={`${ui.eyebrow} ${styles.awaitingEyebrow}`}>{t('myEvents.awaiting')}</div>
      <h2 className={styles.nextTitle}>{event.name}</h2>
      <div className={ui.muted}>
        {[formatEventDates(event.startsOn, event.endsOn, locale), event.replyDeadline ? t('myEvents.awaitingDue', { date: formatDayMonth(event.replyDeadline, locale) }) : null]
          .filter(Boolean)
          .join(' · ')}
      </div>
      <AnswerForm choirId={choirId} event={event} />
    </section>
  );
}
